import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button, Stack, Typography } from '@mui/material';
import { DateTimePicker, DatePicker } from '@mui/x-date-pickers';

import service from '../../FleetReports/service';
import { useBoundStore } from '../../../../stores/index';
import moment from 'moment';

const isWeekend = (date) => {
  const dayOfWeek = moment(date).day();
  return dayOfWeek === 0 || dayOfWeek === 6; // Sunday (0) and Saturday (6)
};

function FleetReportsDrawer() {
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const initialBeginDate = new Date();
  initialBeginDate.setHours(3, 5, 0, 0); // Set the time to 03:05:00

  const initialEndDate = new Date();
  initialEndDate.setHours(23, 59, 59, 999); // Set the time to 23:59:59.999

  const [beginDate, setBeginDate] = useState(initialBeginDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const [selectedValue, setSelectedValue] = useState({});

  const toggleRightDrawer = useBoundStore((state) => state.toggleRightDrawer);

  const getTransportations = async () => {
    setIsLoading(true);
    const params = {
      param: {
        sort: 'createdAt,desc',
        t0: beginDate.toISOString(),
        t1: endDate.toISOString(),
      },
      vehicleId: selectedValue.id,
    };
    const result =
      await service.getTransportationsByVehicleIdAndEndDateBetween(params);
    setIsLoading(false);
  };

  useEffect(() => {
    if (selectedValue?.id) {
      getTransportations();
    }
  }, [selectedValue, beginDate, endDate]);

  const saveFormData = async () => {
    setIsLoading(true);

    const payload = {
      beginDate: beginDate.toISOString(),
      endDate: endDate.toISOString(),
      userId: JSON.parse(localStorage.getItem('user'))?.user?.id,
    };

    const result = await service.saveReportingRequest(payload);

    if ([200, 201].includes(result?.status)) {
      //setFieldsData(fields())
      toast.success('Rapor oluşturulduğunda listenize eklenecektir');
    } else {
      toast.error('Rapor oluşturulurken bir hata oluştu');
    }

    setIsLoading(false);

    navigate('/kavi/fleet-safety-reports');
  };

  const handleBeginDateChange = (date) => {
    // Set the time to 03:05:00 (GMT +3)
    date.setHours(3, 5, 0, 0);
    setBeginDate(date);

    const endDate = new Date(date.getTime() + 6 * 24 * 60 * 60 * 1000);

    // Calculate the end date as 6 days after the begin date
    if (endDate > Date.now()) {
      const endDate = new Date();
      endDate.setHours(23, 59, 59, 999);
      setEndDate(endDate);
    } else {
      endDate.setHours(23, 59, 59, 999);
      setEndDate(endDate);
    }
  };

  const handleEndDateChange = (date) => {
    // Set the time to 23:59:59
    date.setHours(23, 59, 59, 999);
    setEndDate(date);
  };

  return (
    <>
      <div style={{ padding: '20px' }}>
        <Stack spacing={2} direction="column" width="100%">
          <div className="text-center">
            <Typography variant="h6" color="initial">
              Yerlem Kavi Filo Raporu
            </Typography>
          </div>
          <div className="flex flex-row max-lg:flex-wrap max-lg:wrap justify-between">
            <div className="my-2 lg:mr-2 w-full">
              <DatePicker
                label="Başlangıç Tarihi"
                ampm="false"
                value={beginDate}
                // shouldDisableDate={isWeekend}
                inputFormat="dd/MM/yyyy hh:mm"
                onChange={handleBeginDateChange}
                maxDate={Date.now()}
                slotProps={{
                  textField: {
                    sx: {
                      width: '100%',
                    },
                  },
                }}
              />
            </div>
            <div className="my-2 lg:ml-2 w-full">
              <DatePicker
                label="Bitiş Tarihi"
                ampm="false"
                value={endDate}
                // shouldDisableDate={isWeekend}
                onChange={handleEndDateChange}
                inputFormat="dd/MM/yyyy hh:mm"
                maxDate={endDate}
                minDate={beginDate}
                slotProps={{
                  textField: {
                    sx: {
                      width: '100%',
                    },
                  },
                }}
              />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gridGap: 10,
              marginTop: 30,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                saveFormData();
                toast.success('Rapor isteği oluşturuldu.');
                toggleRightDrawer();
                navigate('/kavi/fleet-safety-reports');
              }}
              disabled={isLoading}
            >
              <div className="flex items-center justify-center">
                İstek Oluştur{' '}
                {isLoading && (
                  <div role="status">
                    <svg
                      aria-hidden="true"
                      className="w-4 h-4 mx-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                    <span className="sr-only">Loading...</span>
                  </div>
                )}
              </div>
            </Button>
          </div>
        </Stack>
      </div>
    </>
  );
}

export default FleetReportsDrawer;
